import React, { useState, useRef } from 'react';
import { Upload, X, CheckCircle, AlertCircle, Image as ImageIcon } from 'lucide-react';

const PhotoUpload = ({
                         campLocationId, // Required prop for camp location ID
                         onUploadSuccess,
                         onUploadError,
                         maxFiles = 5,
                         uploadEndpoint = '/otgservices/UploadSiteImage.php',
                         userId = null, // Optional user ID
                         allowFeaturedSelection = false, // Whether to show featured photo option
                         defaultPhotoType = 'general' // Default photo type
                     }) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadResults, setUploadResults] = useState([]);
    const [caption, setCaption] = useState('');
    const [photoType, setPhotoType] = useState(defaultPhotoType);
    const [isFeatured, setIsFeatured] = useState(false);
    const [displayOrder, setDisplayOrder] = useState('');
    const [takenDate, setTakenDate] = useState('');
    const [cameraInfo, setCameraInfo] = useState('');
    const fileInputRef = useRef(null);

    // Validate required props
    if (!campLocationId) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <span className="ml-2 text-red-800">Camp Location ID is required for photo uploads</span>
                </div>
            </div>
        );
    }

    const handleFileSelect = (files) => {
        const fileArray = Array.from(files).slice(0, maxFiles);
        setSelectedFiles(fileArray);
        setUploadResults([]);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        handleFileSelect(files);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const uploadFiles = async () => {
        if (selectedFiles.length === 0) return;

        setUploading(true);
        setUploadProgress(0);
        const results = [];

        for (let i = 0; i < selectedFiles.length; i++) {
            const file = selectedFiles[i];

            try {
                const result = await uploadSingleFile(file, (progress) => {
                    const overallProgress = ((i + progress / 100) / selectedFiles.length) * 100;
                    setUploadProgress(overallProgress);
                });

                results.push({
                    file: file.name,
                    success: true,
                    data: result.data
                });

                // Call success callback for each successful upload
                if (onUploadSuccess) {
                    onUploadSuccess(result.data);
                }

            } catch (error) {
                results.push({
                    file: file.name,
                    success: false,
                    error: error.message
                });

                // Call error callback
                if (onUploadError) {
                    onUploadError(error.message, file.name);
                }
            }
        }

        setUploadResults(results);
        setUploading(false);
        setUploadProgress(100);

        // Clear selected files after upload
        setTimeout(() => {
            setSelectedFiles([]);
            setUploadProgress(0);
            setCaption('');
            setPhotoType(defaultPhotoType);
            setIsFeatured(false);
            setDisplayOrder('');
            setTakenDate('');
            setCameraInfo('');
        }, 2000);
    };

    const uploadSingleFile = (file, progressCallback) => {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('photo', file);
            formData.append('camp_location_id', campLocationId);

            if (userId) {
                formData.append('user_id', userId);
            }

            if (caption && caption.trim() !== '') {
                formData.append('caption', caption.trim());
            }

            if (photoType) {
                formData.append('photo_type', photoType);
            }

            if (isFeatured) {
                formData.append('is_featured', 'true');
            }

            if (displayOrder && displayOrder.trim() !== '') {
                formData.append('display_order', displayOrder);
            }

            if (takenDate && takenDate.trim() !== '') {
                formData.append('taken_date', takenDate);
            }

            if (cameraInfo && cameraInfo.trim() !== '') {
                formData.append('camera_info', cameraInfo);
            }

            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable) {
                    const progress = (e.loaded / e.total) * 100;
                    progressCallback(progress);
                }
            });

            xhr.addEventListener('load', () => {
                try {
                    const response = JSON.parse(xhr.responseText);
                    if (response.success) {
                        resolve(response);
                    } else {
                        reject(new Error(response.error?.message || 'Upload failed'));
                    }
                } catch (e) {
                    reject(new Error('Invalid server response'));
                }
            });

            xhr.addEventListener('error', () => {
                reject(new Error('Network error occurred'));
            });

            xhr.open('POST', uploadEndpoint);
            xhr.send(formData);
        });
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <div className="max-w-2xl mx-auto p-6">
            <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-2xl font-bold mb-6 text-gray-800">Upload Photos</h2>

                {/* Upload Area */}
                <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                        selectedFiles.length > 0
                            ? 'border-blue-400 bg-blue-50'
                            : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p className="text-lg font-medium text-gray-700 mb-2">
                        {selectedFiles.length > 0
                            ? `${selectedFiles.length} file(s) selected`
                            : 'Click to select files or drag and drop'
                        }
                    </p>
                    <p className="text-sm text-gray-500">
                        Supported formats: JPG, PNG, GIF, WEBP (Max 10MB each)
                    </p>
                </div>

                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleFileSelect(e.target.files)}
                    className="hidden"
                />

                {/* Photo Details Form */}
                <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">Photo Details</h3>

                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Photo Type
                                </label>
                                <select
                                    value={photoType}
                                    onChange={(e) => setPhotoType(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                >
                                    <option value="general">General</option>
                                    <option value="campsite">Campsite</option>
                                    <option value="amenity">Amenity</option>
                                    <option value="view">View</option>
                                    <option value="access_road">Access Road</option>
                                    <option value="entrance">Entrance</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Display Order
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    value={displayOrder}
                                    onChange={(e) => setDisplayOrder(e.target.value)}
                                    placeholder="Auto-generated if empty"
                                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Taken Date
                                </label>
                                <input
                                    type="date"
                                    value={takenDate}
                                    onChange={(e) => setTakenDate(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Camera Info
                                </label>
                                <input
                                    type="text"
                                    value={cameraInfo}
                                    onChange={(e) => setCameraInfo(e.target.value)}
                                    placeholder="e.g., iPhone 14 Pro, Canon EOS R5"
                                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Caption
                            </label>
                            <textarea
                                value={caption}
                                onChange={(e) => setCaption(e.target.value)}
                                placeholder="Enter a caption for the photo(s)"
                                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                rows="2"
                            />
                        </div>

                        {allowFeaturedSelection && (
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="isFeatured"
                                    checked={isFeatured}
                                    onChange={(e) => setIsFeatured(e.target.checked)}
                                    className="h-4 w-4 text-blue-600"
                                />
                                <label htmlFor="isFeatured" className="ml-2 text-sm text-gray-700">
                                    Set as featured photo for this camp
                                </label>
                            </div>
                        )}

                        <div className="text-xs text-gray-500">
                            Camp Location ID: {campLocationId}
                            {userId && ` • User ID: ${userId}`}
                        </div>
                    </div>
                </div>

                {/* Selected Files List */}
                {selectedFiles.length > 0 && (
                    <div className="mt-6">
                        <h3 className="text-lg font-medium mb-3">Selected Files:</h3>
                        <div className="space-y-2">
                            {selectedFiles.map((file, index) => (
                                <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                                    <div className="flex items-center">
                                        <ImageIcon className="h-5 w-5 text-gray-400 mr-3" />
                                        <div>
                                            <p className="font-medium text-gray-800">{file.name}</p>
                                            <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => removeFile(index)}
                                        className="text-red-500 hover:text-red-700 p-1"
                                        disabled={uploading}
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Progress Bar */}
                {uploading && (
                    <div className="mt-6">
                        <div className="flex justify-between items-center mb-2">
                            <span className="text-sm font-medium text-gray-700">Uploading...</span>
                            <span className="text-sm text-gray-500">{Math.round(uploadProgress)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress}%` }}
                            ></div>
                        </div>
                    </div>
                )}

                {/* Upload Button */}
                <div className="mt-6">
                    <button
                        onClick={uploadFiles}
                        disabled={selectedFiles.length === 0 || uploading}
                        className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                            selectedFiles.length === 0 || uploading
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                    >
                        {uploading ? 'Uploading...' : `Upload ${selectedFiles.length} File(s)`}
                    </button>
                </div>

                {/* Upload Results */}
                {uploadResults.length > 0 && (
                    <div className="mt-6">
                        <h3 className="text-lg font-medium mb-3">Upload Results:</h3>
                        <div className="space-y-3">
                            {uploadResults.map((result, index) => (
                                <div key={index} className={`p-4 rounded-lg border ${
                                    result.success
                                        ? 'bg-green-50 border-green-200'
                                        : 'bg-red-50 border-red-200'
                                }`}>
                                    <div className="flex items-start">
                                        {result.success ? (
                                            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3" />
                                        ) : (
                                            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-3" />
                                        )}
                                        <div className="flex-1">
                                            <p className="font-medium">
                                                {result.success ? '✓' : '✗'} {result.file}
                                            </p>
                                            {result.success ? (
                                                <div className="text-sm text-green-700 mt-1">
                                                    <p>Uploaded as: {result.data.filename}</p>
                                                    <p>Size: {result.data.file_size_formatted}</p>
                                                    <p>Dimensions: {result.data.dimensions.width}×{result.data.dimensions.height}</p>
                                                    <p>Camp: {result.data.camp_location_id}</p>
                                                    {result.data.photo_id && <p>Photo ID: {result.data.photo_id}</p>}
                                                    {result.data.caption && <p>Caption: {result.data.caption}</p>}
                                                    {result.data.is_primary && <p className="font-medium">✭ Primary Photo</p>}
                                                    {result.data.database_warning && (
                                                        <p className="text-orange-600 mt-1">⚠️ {result.data.database_warning}</p>
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="text-sm text-red-700 mt-1">{result.error}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Preview successful uploads */}
                                    {result.success && (
                                        <div className="mt-3">
                                            <img
                                                src={result.data.file_path}
                                                alt="Uploaded preview"
                                                className="max-w-xs max-h-48 rounded-lg shadow-sm"
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PhotoUpload;